import {
  AIStarsIcon,
  AdminSelect,
  Button,
  Disclaimer,
  Div,
  Icon,
  P,
  TextButton,
  lightTheme,
} from '@vgrubs/components';
import { targetMonthOptions } from '../helpers/repaymentSuggestionLabels';

type Props = {
  /** Months from now to repay in; null = no target. */
  target: number | null;
  onTargetChange: (target: number | null) => void;
  /** The AI is working: the button spins and takes no clicks. */
  busy: boolean;
  onSuggest: () => void;
  /** Shown once there is an answer, to reread it without paying again. */
  onShowLast?: () => void;
  /** The form was just filled from the suggestion. */
  applied: boolean;
  /** What the form's button says, for the "nothing saved yet" line. */
  saveLabel: string;
};

/**
 * The way into the AI suggestion, at the top of the plan form: an optional
 * month to repay by and the button. Whatever is ticked in the form goes along,
 * so the answer compares it with the AI's plan.
 */
export const SuggestPlanBox = ({
  target,
  onTargetChange,
  busy,
  onSuggest,
  onShowLast,
  applied,
  saveLabel,
}: Props) => (
  // Left-aligned like the method rows: the modal centres its text.
  <Div $box $p="12px 16px" $flex $column $gap="12px" $textAlignLeft>
    <div>
      <Div $flex $alignCenter $gap="8px">
        <Icon>
          <AIStarsIcon />
        </Icon>
        <P $m="0" $bold>
          Not sure what to set?
        </P>
      </Div>
      <Disclaimer $m="2px 0 0" $color={lightTheme.lightGray}>
        {applied
          ? `Filled in from the AI suggestion. Nothing is saved until ${saveLabel}.`
          : 'AI suggests the methods and amounts from the last 3 full months of orders, deliveries and the vPay Card. Whatever you tick below is compared with it.'}
      </Disclaimer>
    </div>

    {/* The button lines up with the field, not with its label. */}
    <Div $flex $alignEnd $gap="12px" $columnSM $alignStartSM>
      <Div $width="100%" $minWidth="0">
        <AdminSelect
          label="Repay by (optional)"
          value={target ?? ''}
          onChange={(event) =>
            onTargetChange(
              event.target.value ? Number(event.target.value) : null
            )
          }
          options={
            <>
              <option value="">Any time</option>
              {targetMonthOptions().map((option) => (
                <option key={option.months} value={option.months}>
                  {option.label}
                </option>
              ))}
            </>
          }
        />
      </Div>
      <Button
        $blueGradient
        $selected
        $w="180px"
        $wSM="100%"
        style={{ flex: 'none' }}
        $loading={busy}
        disabled={busy}
        onClick={onSuggest}
      >
        <Div $flex $center $gap="8px">
          <Icon>
            <AIStarsIcon />
          </Icon>
          Suggest with AI
        </Div>
      </Button>
    </Div>

    {onShowLast && !busy && (
      <TextButton
        color={lightTheme.primary}
        $underline
        style={{ width: 'auto', height: 'auto', alignSelf: 'center' }}
        onClick={onShowLast}
      >
        Show the last suggestion
      </TextButton>
    )}
  </Div>
);
